import {
  combineNumber,
  fixedParseFloat,
  normalizeDecimal,
  PATHNAME,
  validateDecimal,
  validateDecimalPoint,
} from '@/utils';

const {
  DEFAULT: { LOCALE: DEFAULT_LOCALE },
  QUERY_PARAM: {
    SYMBOL: { DOT: DOT_SYMBOL },
  },
} = PATHNAME;

const numberFormat = (
  value: number,
  options: Intl.NumberFormatOptions = {
    style: 'decimal',
  },
  addonAfter = '',
  locales: string | string[] = DEFAULT_LOCALE,
) => new Intl.NumberFormat(locales, options).format(value).concat(addonAfter);

const dateTimeFormat = (
  date: Date | number,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  },
  addonAfter = '',
  locales: string | string[] = DEFAULT_LOCALE,
) => new Intl.DateTimeFormat(locales, options).format(date).concat(addonAfter);

const discountPercentage = (
  originalPrice: number,
  price: number,
  addonAfter = '',
  locales: string | string[] = DEFAULT_LOCALE,
) =>
  numberFormat(
    (1 / originalPrice) * (originalPrice - price),
    { style: 'percent' },
    addonAfter,
    locales,
  );

const localeSortByKey = <T extends Record<K, string>, K extends keyof T>(
  data: T[],
  key: K,
  type: 'sort' | 'toSorted' = 'toSorted',
  options?: Intl.CollatorOptions,
  locales: string | string[] = DEFAULT_LOCALE,
): T[] =>
  data[type]((a, b) =>
    new Intl.Collator(locales, options).compare(a[key], b[key]),
  );

const formatCurrency = (
  value: string | number | bigint,
  decimals = 0,
  allowCombine = false,
  allowNegative = false,
  prefix = '',
  suffix = '',
) => {
  let numeric: string | number | bigint = BigInt(0);
  let formatted = '';

  if (typeof value === 'string') {
    const showDecimals = decimals > 0;
    const normalizedValue = normalizeDecimal(value);

    if (validateDecimal(normalizedValue)) {
      if (showDecimals && validateDecimalPoint(normalizedValue)) {
        numeric = normalizedValue;
        formatted = normalizedValue;
      } else {
        try {
          const [integerPart = '0', fractionalPart] =
            normalizedValue.split(DOT_SYMBOL);

          numeric = BigInt(integerPart);

          if (showDecimals && fractionalPart !== undefined) {
            formatted = `${numeric.toString()}${DOT_SYMBOL}${fractionalPart.substring(0, decimals)}`;
            numeric = parseFloat(formatted);
          } else {
            formatted = numeric.toString();
          }
        } catch {
          numeric = fixedParseFloat(parseFloat(normalizedValue), decimals);
          formatted = numeric.toString();
        }
      }
    }
  } else if (typeof value === 'bigint') {
    numeric = value;
    formatted = value.toString();
  } else {
    try {
      if (value > Number.MAX_SAFE_INTEGER) {
        numeric = BigInt(Math.floor(value));
        formatted = numeric.toString();
      } else {
        numeric = fixedParseFloat(value, decimals);
        formatted = numeric.toString();
      }
    } catch {
      numeric = fixedParseFloat(value, decimals);
      formatted = numeric.toString();
    }
  }

  if (
    !allowNegative &&
    (((typeof numeric === 'string' || typeof numeric === 'number') &&
      Number(numeric) < 0) ||
      (typeof numeric === 'bigint' && numeric < BigInt(0)))
  ) {
    numeric =
      typeof numeric === 'bigint' ? -numeric : Math.abs(Number(numeric));
    formatted = numeric.toString();
  }

  if (allowCombine) {
    const hasDecimalPoint = formatted.includes(DOT_SYMBOL);
    const [integerPart, fractionalPart] = formatted.split(DOT_SYMBOL);

    formatted = `${combineNumber(integerPart)}${hasDecimalPoint ? DOT_SYMBOL : ''}${fractionalPart || ''}`;
  }

  return {
    numeric,
    formatted: `${prefix}${formatted}${suffix}`,
  };
};

export {
  dateTimeFormat,
  discountPercentage,
  formatCurrency,
  localeSortByKey,
  numberFormat,
};
